import { collectPages } from "../../shared/api/collect-pages.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requireData, TradingEngineError } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import { pollOperation } from "../../shared/api/operation-polling.js";
import { usdToMinorUnits } from "../../shared/lib/usd.js";
import { botRevisionView, botView, type BotState, type BotRevisionView } from "./models.js";
import { botDecisionTreeView } from "./decision-tree-models.js";

type BotMutation = components["schemas"]["BotMutation"];
type Operation = components["schemas"]["Operation"];
type BotAction = "archive" | "ready" | "start" | "stop";

export const botKeys = {
  all: ["bots"] as const,
  detail: (id: string) => ["bots", id] as const,
  decisionTree: (id: string) => ["bots", id, "decision-tree"] as const,
  revisions: (id: string) => ["bots", id, "revisions"] as const,
};

export async function settleOperation(
  api: ReturnType<typeof useApi>,
  operation: Operation,
): Promise<Operation> {
  const settled =
    operation.status !== "running" ? operation : await pollOperation(api, operation.id);
  if (settled.status === "failed") {
    throw new TradingEngineError(
      settled.problem?.detail ?? "TradingEngine operation failed.",
      settled.problem?.status ?? 422,
      settled.problem?.code ?? "OPERATION_FAILED",
      settled.problem?.correlationId ?? null,
      settled.problem?.errors,
    );
  }
  return settled;
}

export function useBots(filters: { symbol?: string; state?: BotState }) {
  const api = useApi();
  return useQuery({
    queryKey: [...botKeys.all, "list", filters],
    queryFn: async ({ signal }) => {
      const page = await collectPages(async (cursor) =>
        requireData(
          await api.GET("/bots", {
            params: { query: { limit: 100, ...filters, ...(cursor === "" ? {} : { cursor }) } },
            signal,
          }),
        ),
      );
      return page.items.map(botView);
    },
  });
}

export function useBot(botId: string) {
  const api = useApi();
  return useQuery({
    enabled: botId !== "",
    queryKey: botKeys.detail(botId),
    queryFn: async ({ signal }) =>
      botView(requireData(await api.GET("/bots/{botId}", { params: { path: { botId } }, signal }))),
  });
}

export function useBotRevisions(botId: string) {
  const api = useApi();
  return useQuery({
    enabled: botId !== "",
    queryKey: botKeys.revisions(botId),
    queryFn: async ({ signal }) => {
      const page = requireData(
        await api.GET("/revisions", {
          params: { query: { botId, limit: 100 } },
          signal,
        }),
      );
      return page.items.map(botRevisionView).sort((a, b) => b.revision - a.revision);
    },
  });
}

export function useBotDecisionTree(botId: string) {
  const api = useApi();
  return useQuery({
    enabled: botId !== "",
    queryKey: botKeys.decisionTree(botId),
    queryFn: async ({ signal }) =>
      botDecisionTreeView(
        requireData(
          await api.GET("/bots/{botId}/decision-tree", {
            params: { path: { botId } },
            signal,
          }),
        ),
      ),
  });
}

export function useCreateBot() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: BotMutation) => {
      const accepted = requireData(
        await api.POST("/bots", {
          body,
          params: { header: { "x-idempotency-key": crypto.randomUUID() } },
        }),
      );
      return settleOperation(api, accepted.operation);
    },
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: botKeys.all }),
  });
}

export function useUpdateBot(botId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      expectedVersion,
    }: {
      body: BotMutation;
      expectedVersion: number;
    }) => {
      const accepted = requireData(
        await api.PUT("/bots/{botId}", {
          body: { ...body, expectedVersion },
          params: {
            header: { "x-idempotency-key": crypto.randomUUID() },
            path: { botId },
          },
        }),
      );
      return settleOperation(api, accepted.operation);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: botKeys.all }),
        queryClient.invalidateQueries({ queryKey: botKeys.detail(botId) }),
        queryClient.invalidateQueries({ queryKey: botKeys.decisionTree(botId) }),
        queryClient.invalidateQueries({ queryKey: botKeys.revisions(botId) }),
      ]);
    },
  });
}

export function useBotAction(botId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      action,
      brokerAccountId,
      expectedVersion,
    }: {
      action: BotAction;
      brokerAccountId?: string;
      expectedVersion: number;
    }) => {
      const accepted = requireData(
        await api.POST("/bots/{botId}/actions/{action}", {
          body: {
            expectedVersion,
            ...(brokerAccountId === undefined ? {} : { brokerAccountId }),
          },
          params: {
            header: { "x-idempotency-key": crypto.randomUUID() },
            path: { action, botId },
          },
        }),
      );
      return settleOperation(api, accepted.operation);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: botKeys.all }),
        queryClient.invalidateQueries({ queryKey: botKeys.detail(botId) }),
      ]);
    },
  });
}

export function useDuplicateBot() {
  const create = useCreateBot();
  return {
    ...create,
    mutateAsync: async ({ botName, revision }: { botName: string; revision: BotRevisionView }) =>
      create.mutateAsync({
        configuration: {
          execution: revision.execution,
          positionSizing: revision.positionSizing,
          risk: {
            allowShort: revision.risk.allowShort,
            ...(revision.risk.maxPositionQuantity === ""
              ? {}
              : { maxPositionQuantity: revision.risk.maxPositionQuantity }),
            ...(revision.risk.maxPositionNotionalUsd === ""
              ? {}
              : {
                  maxPositionNotional: {
                    currency: "USD",
                    minorUnits: usdToMinorUnits(revision.risk.maxPositionNotionalUsd),
                  },
                }),
          },
          schedule: revision.schedule,
          strategy: revision.strategy,
          symbol: revision.symbol,
        },
        name: `${botName} copy`,
      }),
  };
}
